import {
  Barcode,
  BookUserIcon,
  BoxIcon,
  Building2,
  CoinsIcon,
  FileTextIcon,
  FileWarningIcon,
  FileXIcon,
  GaugeCircleIcon,
  LandmarkIcon,
  ListTree,
  StoreIcon,
  Users2Icon,
} from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { NavMain } from '@/components/nav-main'
import { NavUser } from '@/components/nav-user'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
} from '@/components/ui/sidebar'
import { apiFetcher } from '@/data/fetcher'
import { Settings } from '@/interfaces/Settings'
import { useAuth } from '@/lib/auth'
import { Role } from '@/types/User'

import { Loading } from './Loading'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation()

  const { user } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'AppSidebar',
  })
  const name = `${user?.firstName} ${user?.lastName}`

  const shouldFetchSettings = !!user && !user.roles.includes(Role.admin)
  const { data: settings, isLoading } = useSWR<Settings>(
    shouldFetchSettings ? '/api/v2/settings' : null,
    apiFetcher,
  )

  const data = user && ! user?.roles.includes(Role.admin) ? {
    user: {
      name: name,
      email: user?.email ?? '',
    },
    navDashboard: [
      {
        title: t('sidebar.dashboard'),
        url: '/',
        icon: GaugeCircleIcon,
      },
    ],
    navMain: [
      {
        title: t('sidebar.invoices'),
        url: '/invoices',
        icon: FileTextIcon,
      },
      {
        title: t('sidebar.orders'),
        url: '/orders',
        icon: FileWarningIcon,
      },
      {
        title: t('sidebar.expenses'),
        url: '/expenses',
        icon: FileXIcon,
      },
      {
        title: t('sidebar.contacts'),
        url: '/contacts',
        icon: BookUserIcon,
      },
    ],
    navMoney: [
      settings?.features?.includes('bank') ? {
        title: t('sidebar.bank'),
        url: '/bank',
        icon: LandmarkIcon,
      } : undefined,
      settings?.features?.includes('cashRegistry') ? {
        title: t('sidebar.cashRegister'),
        url: '/cash-register',
        icon: CoinsIcon,
      } : undefined,
    ].filter((item) => item !== undefined),
    // Katalog sdružuje produkty, kategorie a sklady. Každá položka má dál vlastní feature flag.
    navCatalog: [
      settings?.features?.includes('catalog') ? {
        title: t('sidebar.products'),
        url: '/catalog/products',
        icon: Barcode,
      } : undefined,
      settings?.features?.includes('catalog') ? {
        title: t('sidebar.categories'),
        url: '/catalog/categories',
        icon: ListTree,
      } : undefined,
      settings?.features?.includes('warehouse') ? {
        title: t('sidebar.warehouses'),
        url: '/warehouse/warehouses',
        icon: BoxIcon,
        activePrefix: '/warehouse',
      } : undefined,
    ].filter((item) => item !== undefined),
    // E-shop je hlavně nastavení, v sidebaru stačí jedna položka a části se přepínají záložkami.
    navEshop: settings?.features?.includes('eshop') ? [
      {
        title: t('sidebar.eshop'),
        url: '/eshop/delivery-methods',
        icon: StoreIcon,
        activePrefix: '/eshop',
      },
    ] : [],
  } : {
    user: {
      name: user?.email ?? '',
      email: user?.email ?? '',
    },
    navDashboard: [
      {
        title: t('admin.sidebar.dashboard'),
        url: '/',
        icon: GaugeCircleIcon,
      },
      {
        title: t('admin.sidebar.users'),
        url: '/admin/users',
        icon: Users2Icon,
      },
      {
        title: t('admin.sidebar.accounts'),
        url: '/admin/accounts',
        icon: Building2,
      },
    ],
    navMain: [],
    navMoney: [],
    navCatalog: [],
    navEshop: [],
  };

  return (
    <Sidebar
      className="top-(--header-height) bottom-0 h-auto!"
      {...props}
    >
      <SidebarContent className="scrollbar-subtle">
        {isLoading && <Loading />}
        {!isLoading && (
          <>
            <NavMain items={data.navDashboard} />
            <NavMain items={data.navMain} title={t('sidebar.documents')} />
            <NavMain items={data.navMoney} title={t('sidebar.money')} />
            <NavMain items={data.navCatalog} title={t('sidebar.catalog')} />
            <NavMain items={data.navEshop} />
          </>
        )}

      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
    </Sidebar>
  )
}
